<template>
  <div class="appointments-calendar-page">
    <Card
      class="h-full"
      full-height
      no-margin
    >
      <div class="full-calendar-wrapper">
        <!-- Menu -->
        <div
          class="calendar-left h-full overflow-y-auto"
          :class="{ open: menuOpen }"
        >
          <h3 class="p-5">{{ $t('ui.common.filter', 2) }}</h3>
          <div class="p-5 pt-0">
            <Label for="status">{{ $t('ui.common.status', 2) }}</Label>
            <div
              v-for="status in filterArray.statuses"
              :key="status.name"
              class="mb-1"
            >
              <Checkbox
                v-model="status.isChecked"
                :label="status.label"
                :checkbox-color="status.color"
              />
            </div>
          </div>
          <div class="p-5 pt-0 pb-0">
            <FormGroup
              :label="$t('ui.entities.dealership', 2)"
              label-for="dealerships"
            >
              <DealershipSelect
                v-model="filterArray.dealershipsSelected"
                mode="multiple"
                :dealership-ids="accessStore.accessSelected.dealerships"
                default-group-by="org"
                :chips="false"
                fluid
                :placeholder="$t('ui.common.all', 2)"
              />
            </FormGroup>
          </div>
          <div class="p-5 pt-0">
            <FormGroup
              :label="$t('ui.entities.appointment.bookedBy')"
              label-for="created-by"
            >
              <Select
                v-model="filterArray.createdBySelected"
                mode="multiple"
                :options="filterArray.createdByItems"
                label-key="fullName"
                value-key="id"
                searchable
                :chips="true"
                :auto-expand="true"
                :placeholder="$t('ui.common.all', 2)"
              />
            </FormGroup>
          </div>
          <div class="p-5 pt-0">
            <Button
              :label="$t('ui.actions.clear', 2)"
              severity="secondary"
              icon="filter-slash"
              outlined
              @click="clearFilters()"
            />
          </div>
        </div>
        <!-- Main Calendar -->
        <div class="calendar-right h-full min-h-0">
          <Calendar
            v-model:view="calendarView"
            v-model:date="calendarDate"
            class="appointments-calendar h-full"
            :events="visibleEvents"
            :blockouts="DEMO_BLOCKOUTS"
            :loading="eventsLoading"
            event-popover-trigger="click"
            event-open-trigger="dblclick"
            :editable="false"
            :first-day="1"
            @range-change="handleRangeChange"
            @event-click="handleEventClick"
            @event-drop="handleEventDrop"
          >
            <template #toolbar-start>
              <Button
                :label="$t('ui.common.filter')"
                severity="secondary"
                icon="filter"
                outlined
                size="sm"
                @click="toggleMenu"
              />
            </template>
            <template #event-popover="{ event }">
              <fullCalendarContent :appointment-data="toAppointmentMeta(event)" />
            </template>
          </Calendar>
        </div>
        <div
          class="calendar-menu-overlay"
          :class="{ open: menuOpen }"
          @click="toggleMenu"
        ></div>
      </div>
    </Card>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { computed, reactive, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

// composables
import useApiRequest from '@/composables/useApiRequest'

// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useAccessStore } from '@/stores/AccessStore'

// components
import Calendar from '@/components/calendar/Calendar.vue'
import Button from '@/components/button/Button.vue'
import Card from '@/components/card/Card.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Label from '@/components/forms/Label.vue'
import Select from '@/components/forms/Select.vue'
import DealershipSelect from '@/components/forms/DealershipSelect.vue'
import fullCalendarContent from '@/components/unique/full-calendar/fullCalendarContent.vue'

// utilities
import { filterCalendarEvents, mapAppointmentsToCalendarEvents } from '@/utilities/appointments/calendarEvents'
import {
  buildAppointmentsCalendarPath,
  resolveAppointmentsCalendarLocation
} from '@/utilities/appointments/calendarRoute'

// types
import type {
  CalendarAppointmentEvent,
  CalendarAppointmentExtendedProps
} from '@/utilities/appointments/calendarEvents'
import type { AppointmentListItem } from '@/types/domain/appointment'
import type {
  CalendarBlockout,
  CalendarDropPayload,
  CalendarEvent,
  CalendarRangeChangePayload,
  CalendarViewType
} from '@/types/components/calendar'

/**
 * User option shown in the "booked by" filter.
 */
interface CreatedByFilterItem {
  id: number
  fullName: string
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const accessStore = useAccessStore()

const users = useApiRequest()

/** Whether the filter drawer is open */
const menuOpen = ref(false)

/**
 * Toggles the calendar filter drawer.
 */
function toggleMenu(): void {
  menuOpen.value = !menuOpen.value
}

/** Sidebar filter state. Stored as a plain reactive object rather than a ref([]) with extra keys. */
const filterArray = reactive({
  statuses: [
    {
      name: 'unassigned',
      label: t('ui.entities.appointment.status.unassigned'),
      isChecked: true,
      color: 'utility-gray-600'
    },
    {
      name: 'assigned',
      label: t('ui.entities.appointment.status.assigned'),
      isChecked: true,
      color: 'utility-blue-900'
    },
    {
      name: 'cancelled',
      label: t('ui.entities.appointment.status.cancelled'),
      isChecked: true,
      color: 'utility-error-600'
    },
    {
      name: 'no-show',
      label: t('ui.entities.appointment.status.noShow'),
      isChecked: true,
      color: 'utility-error-600'
    },
    {
      name: 'completed',
      label: t('ui.entities.appointment.status.completed'),
      isChecked: true,
      color: 'utility-success-600'
    },
    {
      name: 'ongoing',
      label: t('ui.entities.appointment.status.ongoing'),
      isChecked: true,
      color: 'utility-purple-600'
    }
  ],
  dealershipsSelected: [] as number[],
  createdByItems: [] as CreatedByFilterItem[],
  createdBySelected: [] as number[]
})

/**
 * Loads retain users for the booked-by filter.
 */
async function loadCreatedByUsers(): Promise<void> {
  try {
    const response = await users.send<CreatedByFilterItem[]>({
      endpoint: '/v1/users',
      params: { userType: 'retainUsers', forList: true }
    })

    // Only replace the dropdown when the payload is a real list
    if (Array.isArray(response.data)) {
      filterArray.createdByItems = response.data
    }
  } catch (err: unknown) {
    logRequestError(err)
  }
}

void loadCreatedByUsers()

/** Currently selected dealership ids from access context */
const dealerships = accessStore.selectedDealershipIds

/** Restored view and date, preferring the path, then query, then localStorage */
const resolvedLocation = resolveAppointmentsCalendarLocation({
  view: route.params.view,
  year: route.params.year,
  month: route.params.month,
  day: route.params.day,
  queryView: route.query.view,
  queryDate: route.query.date,
  fallbackView: localStorage.getItem('calendarInitView'),
  fallbackDate: localStorage.getItem('calendarInitDate')
})

const calendarView = ref<CalendarViewType>(resolvedLocation.view)

/** Cursor date as 'YYYY-MM-DD' */
const calendarDate = ref(resolvedLocation.date)

if (typeof route.query.statuses === 'string') {
  const calendarInitStatuses = route.query.statuses.split(',')

  filterArray.statuses.forEach(filter => {
    filter.isChecked = calendarInitStatuses.includes(filter.name)
  })
}

// Force a fetch on first load even if the stored range matches
localStorage.setItem('calendarStartDate', '')

/**
 * Temporary demo blockouts for the current week so blocked styling can be verified.
 * Remove once real availability data is wired up.
 */
const DEMO_BLOCKOUTS: CalendarBlockout[] = [
  // {
  //   id: 'demo-bank-holiday',
  //   title: 'Bank holiday',
  //   start: dayjs().startOf('isoWeek').format('YYYY-MM-DD'),
  //   end: dayjs().startOf('isoWeek').add(1, 'day').format('YYYY-MM-DD'),
  //   allDay: true,
  //   color: 'utility-error-400',
  //   icon: 'ban',
  //   strictness: 'hard'
  // },
  // {
  //   id: 'demo-lunch',
  //   title: 'Lunch',
  //   start: dayjs().startOf('isoWeek').add(2, 'day').hour(12).minute(0).second(0).toISOString(),
  //   end: dayjs().startOf('isoWeek').add(2, 'day').hour(13).minute(0).second(0).toISOString(),
  //   color: 'utility-gray-400',
  //   icon: 'utensils',
  //   strictness: 'hard'
  // },
  // {
  //   id: 'demo-closed',
  //   title: 'Closed',
  //   start: dayjs().startOf('isoWeek').add(4, 'day').hour(16).minute(0).second(0).toISOString(),
  //   end: dayjs().startOf('isoWeek').add(4, 'day').hour(18).minute(0).second(0).toISOString(),
  //   color: 'utility-warning-500',
  //   strictness: 'soft'
  // }
]

/**
 * All appointments for the visible date range.
 * shallowRef avoids wrapping thousands of event objects in Vue proxies.
 */
const allEvents = shallowRef<CalendarAppointmentEvent[]>([])

/** True until the current visible-range request finishes */
const eventsLoading = ref(true)

/** Monotonic id used to ignore stale appointment responses */
let eventsRequestId = 0

/** Events remaining after sidebar filters */
const visibleEvents = computed(() =>
  filterCalendarEvents(allEvents.value, {
    statuses: filterArray.statuses.filter(status => status.isChecked).map(status => status.name),
    dealershipIds: filterArray.dealershipsSelected,
    createdByIds: filterArray.createdBySelected
  })
)

/**
 * Logs API failures without assuming an Axios error shape.
 * @param err - Caught error
 */
function logRequestError(err: unknown): void {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const requestError = err as { response?: { status?: number; data?: unknown } }
    console.error(requestError.response?.status)
    console.error(requestError.response?.data)
    return
  }

  console.error(err)
}

/**
 * Narrows a calendar event's meta back to the appointment popover payload.
 * @param event - Event from the popover slot
 */
function toAppointmentMeta(event: CalendarEvent): CalendarAppointmentExtendedProps | null {
  return (event.meta as CalendarAppointmentExtendedProps | undefined) ?? null
}

/**
 * Fetches appointments for the visible calendar range.
 * @param start - Range start ISO string
 * @param end - Range end ISO string
 */
async function getEvents(start: string, end: string): Promise<void> {
  const requestId = ++eventsRequestId
  eventsLoading.value = true
  const urlParams: Record<string, unknown> = {
    dealerships,
    start: dayjs(start).format('YYYY-MM-DD'),
    end: dayjs(end).format('YYYY-MM-DD')
  }

  // Sales executives only see appointments assigned to them
  if (authStore.roles.some(role => Number(role) === 10) && authStore.roles.length === 1) {
    urlParams.assignedTo = authStore.details.id
  }

  try {
    const response = await useApiRequest().send<AppointmentListItem[]>({
      endpoint: '/v1/appointments',
      params: urlParams
    })

    // Ignore outdated responses when the user paged the calendar quickly
    if (requestId !== eventsRequestId) {
      return
    }

    allEvents.value = Array.isArray(response.data) ? mapAppointmentsToCalendarEvents(response.data) : []
  } catch (err: unknown) {
    logRequestError(err)
  } finally {
    // A newer request owns the spinner; leaving it spinning avoids a blank flash
    if (requestId === eventsRequestId) {
      eventsLoading.value = false
    }
  }
}

/**
 * Loads events when the visible date range changes.
 * @param payload - Range emitted by the calendar
 */
function handleRangeChange(payload: CalendarRangeChangePayload): void {
  // Fetch only when the visible range actually changed
  if (payload.start !== localStorage.getItem('calendarStartDate') && dealerships) {
    void getEvents(payload.start, payload.end)
  } else if (!dealerships) {
    // Nothing to load when the user has no dealerships selected
    eventsLoading.value = false
  }

  localStorage.setItem('calendarInitView', payload.view)
  localStorage.setItem('calendarInitDate', calendarDate.value)
  localStorage.setItem('calendarStartDate', payload.start)
  localStorage.setItem('calendarEndDate', payload.end)
}

/**
 * Opens the appointment when an event is double-clicked.
 * @param event - Calendar event the host asked to open
 */
function handleEventClick(event: CalendarEvent): void {
  try {
    // Events without a url are not appointment records
    if (event.url) {
      router.push(event.url)
    }
  } catch {
    return
  }
}

/**
 * Placeholder drop handler: shows the proposed time and reverts.
 * TODO: PATCH /v1/appointments/{id}/reschedule and only revert on failure.
 * @param payload - Drop result from the calendar
 */
function handleEventDrop(payload: CalendarDropPayload): void {
  window.alert(`Move "${payload.event.title}" to ${payload.start}`)
  payload.revert()
}

/**
 * Resets sidebar filters and the route query.
 */
function clearFilters(): void {
  filterArray.statuses.forEach(status => {
    status.isChecked = true
  })

  filterArray.dealershipsSelected = []
  filterArray.createdBySelected = []

  router.replace({ path: route.path, query: {} })
}

/**
 * Drops legacy `view` and `date` query keys once they live in the path.
 * @param query - Current route query
 */
function queryWithoutCalendarLocation(query: typeof route.query): typeof route.query {
  if (!('view' in query) && !('date' in query)) {
    return query
  }

  const nextQuery = { ...query }
  delete nextQuery.view
  delete nextQuery.date

  return nextQuery
}

/**
 * Writes the current view and date into the path so the page is shareable.
 */
function syncCalendarRoute(): void {
  try {
    const path = buildAppointmentsCalendarPath(calendarView.value, calendarDate.value)
    const query = queryWithoutCalendarLocation(route.query)
    const queryUnchanged = Object.keys(query).length === Object.keys(route.query).length

    // Skip replace when the URL is already canonical
    if (route.path === path && queryUnchanged) {
      return
    }

    router.replace({ path, query })
  } catch {
    return
  }
}

syncCalendarRoute()

watch([calendarView, calendarDate], () => {
  localStorage.setItem('calendarInitView', calendarView.value)
  localStorage.setItem('calendarInitDate', calendarDate.value)
  syncCalendarRoute()
})

watch(
  () => [
    route.params.view,
    route.params.year,
    route.params.month,
    route.params.day,
    route.query.view,
    route.query.date
  ],
  () => {
    const next = resolveAppointmentsCalendarLocation({
      view: route.params.view,
      year: route.params.year,
      month: route.params.month,
      day: route.params.day,
      queryView: route.query.view,
      queryDate: route.query.date,
      fallbackView: calendarView.value,
      fallbackDate: calendarDate.value
    })

    // Apply only real back/forward or inbound-link changes
    if (next.view !== calendarView.value) {
      calendarView.value = next.view
    }

    if (next.date !== calendarDate.value) {
      calendarDate.value = next.date
    }

    syncCalendarRoute()
  }
)
</script>

<style>
.appointments-calendar-page {
  display: flex;
  flex-direction: column;
  /* Page content already has padding: s-6 top and s-12 bottom */
  height: calc(100dvh - var(--s-6) - var(--s-12));
  min-height: 0;
}

/* BlockUI defaults to inline-block, which collapses percentage heights */
.appointments-calendar-page .block-ui-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.appointments-calendar-page .card {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
}

.appointments-calendar-page .full-calendar-wrapper {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.calendar-left {
  width: 250px;
  flex-shrink: 0;
  border-right: 1px solid var(--border-secondary);
  background: var(--bg-primary);
  z-index: 11;
  transition: all 0.5s ease-in-out;
  border-radius: 0.428rem 0 0 0.428rem;
  position: absolute;
  left: -251px;
}

.calendar-left.open {
  left: 0;
}

.calendar-right {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.appointments-calendar {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.calendar-menu-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  border-radius: 0.428rem;
  background-color: #00000033;
  visibility: hidden;
  opacity: 0;
  z-index: 10;
  transition: all 0.6s ease-in-out;
}

.calendar-menu-overlay.open {
  visibility: visible;
  opacity: 1;
}

.appointments-calendar .style-appointment.cancelled {
  text-decoration: line-through;
  color: var(--text-quaternary);
}
</style>
